import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, userApi, setAuthCallbacks } from '../services/api';
import { message } from 'antd';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshingRef = useRef(false);
  const refreshSubscribersRef = useRef([]);

  const subscribeTokenRefresh = (callback) => {
    refreshSubscribersRef.current.push(callback);
  };

  const onTokenRefreshed = (newToken) => {
    refreshSubscribersRef.current.forEach(callback => callback(newToken));
    refreshSubscribersRef.current = [];
  };

  const logout = useCallback((showMessage = true) => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    if (showMessage) {
      message.success('已登出');
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      return new Promise(resolve => {
        subscribeTokenRefresh(resolve);
      });
    }

    isRefreshingRef.current = true;
    try {
      const response = await authApi.refreshToken();
      const { expires_in } = response.data.data;

      setToken('refreshed');
      onTokenRefreshed('refreshed');

      return 'refreshed';
    } catch (error) {
      logout(false);
      return null;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [logout]);

  const login = useCallback(async (username, password) => {
    try {
      const response = await authApi.login(username, password);

      const { user: userData } = response.data.data;
      
      setToken('authenticated');
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true, forceChangePassword: userData.force_change_password };
    } catch (error) {
      const errorMessage = error.response?.data?.message || '登录失败';
      return { success: false, error: errorMessage };
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  useEffect(() => {
    setAuthCallbacks(logout, refreshAccessToken);
  }, [logout, refreshAccessToken]);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const response = await userApi.getCurrent();
        const userData = response.data.data;
        if (userData) {
          setUser(userData);
          setToken('authenticated');
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        }
      } catch (e) {
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;